import * as yup from 'yup';

import {IS_NG} from 'core/constants/env';

const baseLabelValueSchema = yup
    .object({
        label: yup
            .string()
            .nullable()
            .notRequired(),
        value: yup
            .string()
            .nullable()
            .notRequired(),
    })
    .nullable()
    .notRequired();

const selectShape = baseLabelValueSchema;

const emptyStringToUndefined = (value: unknown, originalValue: unknown) =>
    originalValue === '' ? undefined : value;

export const lineSchema = yup.object().shape({
    invoiceLineType: yup
        .string()
        .oneOf(['Marked', 'Unmarked'])
        .notRequired(),
    codeGtin: yup
        .string()
        .nullable()
        .when('invoiceLineType', {
            is: 'Marked',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable().notRequired(),
        }),
    sellersItemId: yup
        .string()
        .nullable()
        .notRequired(),
    buyersItemId: yup
        .string()
        .nullable()
        .notRequired(),
    description: yup
        .string()
        .transform(emptyStringToUndefined)
        .when('$lineType', {
            is: 'transport',
            then: (schema) => schema.nullable().notRequired(),
            otherwise: (schema) => schema.required(),
        }),

    qty: yup
        .number()
        .transform(emptyStringToUndefined)
        .positive()
        .when('$lineType', {
            is: 'transport',
            then: (schema) => schema.nullable().notRequired(),
            otherwise: (schema) => schema.required(),
        }),
    price: yup
        .number()
        .transform(emptyStringToUndefined)
        .positive()
        .when('$lineType', {
            is: 'transport',
            then: (schema) => schema.nullable().notRequired(),
            otherwise: (schema) => schema.required(),
        }),
    vatCategory: selectShape.when('$lineType', {
        is: 'transport',
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required(),
    }),
    vat: yup
        .number()
        .transform(emptyStringToUndefined)
        .when('$lineType', {
            is: 'transport',
            then: (schema) => schema.nullable().notRequired(),
            otherwise: (schema) => schema.required(),
        }),

    serviceCategory: IS_NG ? selectShape.required() : selectShape.nullable(),
});

export const generalInfoSchema = yup.object({
    test: yup
        .array()
        .of(lineSchema)
        .min(1),
});

const extraFieldsShape = yup.number().when('$extraFileds', {
    is: true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.nullable(),
});

export const chargesModalSchema = yup.object().shape({
    reason: yup.string().required(),
    amountXaf: yup.number().required(),
    ratePercent: extraFieldsShape,
    baseAmount: extraFieldsShape,
});

type ValidationContext = {
    IS_NG: boolean;
    lineType?: 'unit' | 'transport' | null;
    extraFileds?: boolean;
};

export const createValidationContext = (
    x?: Partial<Omit<ValidationContext, 'IS_NG'>>,
): ValidationContext => ({
    ...x,
    IS_NG,
});

const resolveFieldSchema = (fieldSchema: any, context: ValidationContext) => {
    if (!fieldSchema) {
        return null;
    }

    if (typeof fieldSchema.resolve === 'function') {
        return fieldSchema.resolve({
            context,
        });
    }

    return fieldSchema;
};

const hasRequiredTest = (schema: any) => {
    if (!schema) {
        return false;
    }

    // eslint-disable-next-line no-underscore-dangle
    const exclusiveTests = schema.exclusiveTests ?? schema._exclusive;
    if (exclusiveTests?.required) {
        return true;
    }

    const tests = schema.tests ?? [];
    return tests.some((test: any) => test?.OPTIONS?.name === 'required');
};

export const createIsFieldRequired = (context: ValidationContext, schema?: any) => {
    const resolvedSchema = schema
        ? schema.resolve({context})
        : generalInfoSchema.resolve({context});

    return (name: string) => {
        const fieldSchema = resolvedSchema.fields[name as keyof typeof resolvedSchema.fields];
        const concreteSchema = resolveFieldSchema(fieldSchema, context);

        return hasRequiredTest(concreteSchema);
    };
};
